/**
 * 학생 학습 데이터 요약 컴포넌트
 * Student Learning Data Summary Component
 *
 * 상담 작성 시 학생의 최근 학습 데이터를 표시
 */

import React from 'react';
import {
  StudentDailyRecord,
  FocusMetric,
  StudyGoal,
  PreConsultationMetrics,
} from '../models/CounselingRecord';
import './StudentLearningDataSummary.css';

interface StudentLearningDataSummaryProps {
  dailyRecords?: StudentDailyRecord[];
  focusMetrics?: FocusMetric[];
  studyGoals?: StudyGoal[];
  preConsultationMetrics?: PreConsultationMetrics;
}

export const StudentLearningDataSummary: React.FC<StudentLearningDataSummaryProps> = ({
  dailyRecords = [],
  focusMetrics = [],
  studyGoals = [],
  preConsultationMetrics,
}) => {
  // 감정 레이블 매핑
  const emotionLabels: Record<string, string> = {
    happy: '😊 행복',
    normal: '😐 보통',
    tired: '😫 피곤',
    stressed: '😰 스트레스',
    sleepy: '😴 졸림',
  };

  // 목표 설정자 레이블
  const goalSetterLabels: Record<string, string> = {
    student: '학생',
    staff: '직원',
    parent: '학부모',
  };

  // 목표 기간 레이블
  const goalPeriodLabels: Record<string, string> = {
    daily: '일일',
    weekly: '주간',
    monthly: '월간',
  };

  // 데이터가 없는 경우
  if (dailyRecords.length === 0 && focusMetrics.length === 0 && studyGoals.length === 0) {
    return (
      <div className="student-learning-data-summary">
        <div className="no-data-message">
          <p>📊 학습 데이터가 없습니다.</p>
          <p className="hint">학생이 아직 학습 활동을 기록하지 않았거나 데이터 수집에 실패했습니다.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="student-learning-data-summary">
      <div className="summary-header">
        <h3>📊 최근 7일 학습 데이터</h3>
        <p className="subtitle">상담 시점의 학생 학습 상태를 자동으로 수집한 데이터입니다</p>
      </div>

      {/* 상담 전 지표 (요약) */}
      {preConsultationMetrics && (
        <div className="metrics-overview">
          <h4>📈 학습 요약 지표</h4>
          <div className="metrics-grid">
            <div className="metric-card">
              <div className="metric-label">평균 순공부시간</div>
              <div className="metric-value">{preConsultationMetrics.avg_study_hours_per_day.toFixed(1)}시간/일</div>
            </div>
            <div className="metric-card">
              <div className="metric-label">평균 집중률</div>
              <div className="metric-value">{preConsultationMetrics.avg_focus_rate.toFixed(1)}%</div>
            </div>
            <div className="metric-card">
              <div className="metric-label">목표 달성률</div>
              <div className="metric-value">{preConsultationMetrics.avg_goal_achievement_rate.toFixed(1)}%</div>
            </div>
            <div className="metric-card">
              <div className="metric-label">평균 피로도</div>
              <div className="metric-value">{preConsultationMetrics.avg_fatigue_level.toFixed(1)}/5</div>
            </div>
          </div>
          <p className="metrics-period">
            기간: {preConsultationMetrics.period.start_date} ~ {preConsultationMetrics.period.end_date} ({preConsultationMetrics.total_study_days}일)
          </p>
        </div>
      )}

      {/* 일일 기록 */}
      {dailyRecords.length > 0 && (
        <div className="data-section">
          <h4>📝 일일 기록 ({dailyRecords.length}일)</h4>
          <div className="daily-records-list">
            {dailyRecords.slice(0, 5).map((record) => (
              <div key={record.id} className="daily-record-item">
                <div className="record-date">{record.record_date}</div>
                <div className="record-details">
                  {record.emotion && (
                    <span className="record-badge emotion">
                      {emotionLabels[record.emotion] || record.emotion}
                    </span>
                  )}
                  {record.fatigue_level && (
                    <span className="record-badge fatigue">
                      피로도: {record.fatigue_level}/5
                    </span>
                  )}
                  {record.sleep_hours && (
                    <span className="record-badge sleep">
                      수면: {record.sleep_hours}시간
                    </span>
                  )}
                </div>
                {record.note && <div className="record-note">{record.note}</div>}
              </div>
            ))}
            {dailyRecords.length > 5 && (
              <p className="more-data">외 {dailyRecords.length - 5}개 기록</p>
            )}
          </div>
        </div>
      )}

      {/* 집중률 메트릭 */}
      {focusMetrics.length > 0 && (
        <div className="data-section">
          <h4>🎯 집중률 메트릭 ({focusMetrics.length}일)</h4>
          <div className="focus-metrics-list">
            {focusMetrics.slice(0, 5).map((metric) => (
              <div key={metric.id} className="focus-metric-item">
                <div className="metric-date">{metric.metric_date}</div>
                <div className="metric-stats">
                  <span className="stat-item">
                    순공부: {(metric.total_study_minutes / 60).toFixed(1)}시간
                  </span>
                  {metric.focus_score && (
                    <span className="stat-item focus-score">
                      집중률: {metric.focus_score.toFixed(1)}%
                    </span>
                  )}
                  <span className="stat-item">
                    출입: {metric.entry_count}회
                  </span>
                </div>
              </div>
            ))}
            {focusMetrics.length > 5 && (
              <p className="more-data">외 {focusMetrics.length - 5}개 기록</p>
            )}
          </div>
        </div>
      )}

      {/* 학습 목표 */}
      {studyGoals.length > 0 && (
        <div className="data-section">
          <h4>🎯 활성 학습 목표 ({studyGoals.length}개)</h4>
          <div className="study-goals-list">
            {studyGoals.map((goal) => (
              <div key={goal.id} className="study-goal-item">
                <div className="goal-header">
                  <span className="goal-setter">{goalSetterLabels[goal.goal_setter] || goal.goal_setter}</span>
                  <span className="goal-period">{goalPeriodLabels[goal.goal_period] || goal.goal_period}</span>
                  {goal.achievement_rate !== undefined && (
                    <span className={`goal-achievement ${goal.achievement_rate >= 80 ? 'high' : goal.achievement_rate >= 60 ? 'medium' : 'low'}`}>
                      달성률: {goal.achievement_rate.toFixed(0)}%
                    </span>
                  )}
                </div>
                <div className="goal-details">
                  {goal.daily_study_hours && (
                    <div>목표: 하루 {goal.daily_study_hours}시간 학습</div>
                  )}
                  {goal.weekly_attendance_days && (
                    <div>목표: 주 {goal.weekly_attendance_days}일 출석</div>
                  )}
                  <div className="goal-period-dates">
                    {goal.start_date} ~ {goal.end_date}
                  </div>
                </div>
                {goal.notes && <div className="goal-notes">{goal.notes}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="data-footer">
        <p className="info-text">
          💡 이 데이터는 상담 작성 시점에 자동으로 수집되어 저장됩니다.
          나중에 상담 효과를 측정할 때 기준값으로 사용됩니다.
        </p>
      </div>
    </div>
  );
};

export default StudentLearningDataSummary;
